// src/utils/firebase/authHeader.js
//
// Authorization header for calls to our own `onRequest` Cloud Functions.
//
// WHY THIS EXISTS: `onCall` callables attach the caller's ID token automatically;
// raw `onRequest` endpoints do not. Every place that needed one grew its own
// inline `user.getIdToken()` — about ten copies — and the copies disagreed. The
// M-RETS one (MretsSubmissionModal.vue) was opt-in per call site, so 15 of its 31
// endpoints were being called with no credential at all.
//
// This throws when nobody is signed in, and that is deliberate. The older inline
// helpers returned `{}` instead, which fails OPEN: the request went out
// unauthenticated, and the server — which was not checking — served it anyway.
// The moment the server does enforce, that silent `{}` becomes an unexplained 401
// at the call site with nothing pointing at the real cause. Failing loudly here
// turns a confusing 401 into an accurate "you are not signed in".

import { getAuth } from 'firebase/auth'

/**
 * @param {boolean} [forceRefresh] - mint a fresh token instead of using the
 *   cached one. Only needed right after a claims change; the SDK refreshes on
 *   its own about every hour.
 * @returns {Promise<{Authorization: string}>}
 */
export async function authHeader(forceRefresh = false) {
  const user = getAuth().currentUser
  if (!user) {
    throw new Error('Not signed in — cannot authorize this request.')
  }
  const token = await user.getIdToken(forceRefresh)
  if (!token) {
    throw new Error('Could not obtain an ID token for the signed-in user.')
  }
  return { Authorization: `Bearer ${token}` }
}

/**
 * Convenience for the common JSON POST shape:
 *   headers: await jsonAuthHeaders()
 */
export async function jsonAuthHeaders(forceRefresh = false) {
  return { 'Content-Type': 'application/json', ...(await authHeader(forceRefresh)) }
}
